import React, { useEffect, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import './VideosYTube.css';

// Live, cron-refreshed list first; fall back to the static build asset if the
// API is unreachable (e.g. before the backend is deployed).
const API_BASE      = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const VIDEOS_API    = `${API_BASE}/api/videos`;
const VIDEOS_STATIC = process.env.PUBLIC_URL + '/assets/videos.json';
const CHANNEL_URL = 'https://www.youtube.com/channel/UCkD89TEFWsTZluY6tk84yqg/videos';

async function fetchVideos() {
  for (const url of [VIDEOS_API, VIDEOS_STATIC]) {
    try {
      const r = await fetch(url, { cache: 'no-store' });
      if (r.ok) return await r.json();
    } catch { /* try the next source */ }
  }
  throw new Error('No video source reachable');
}

function thumbFor(id) {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

export default function VideosYTube() {
  const { t } = useT();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let mounted = true;

    fetchVideos()
      .then(data => {
        if (!mounted) return;
        const raw = Array.isArray(data?.videos) ? data.videos : [];
        const items = raw
          .filter(v => v && typeof v.id === 'string' && v.id.length > 0)
          .map(v => ({
            id: v.id,
            title: v.title || '',
            date: v.date || '',
            thumb: v.thumb || thumbFor(v.id),
          }));
        setVideos(items);
        setActive(items[0]?.id || null);
        setError(false);
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="yt-page">
        <span className="yt-eyebrow">{t('videosPage.eyebrow')}</span>
        <h1 className="yt-heading">{t('videosPage.heading')}</h1>
        <p className="yt-status">{t('videosPage.loading')}</p>
      </div>
    );
  }

  if (error || !videos.length) {
    return (
      <div className="yt-page">
        <span className="yt-eyebrow">{t('videosPage.eyebrow')}</span>
        <h1 className="yt-heading">{t('videosPage.heading')}</h1>
        <p className="yt-status">{t('videosPage.error')}</p>
        <p className="yt-status">
          <a
            href={CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--ck-gold)' }}
          >
            YouTube →
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="yt-page">
      <span className="yt-eyebrow">{t('videosPage.eyebrow')}</span>
      <h1 className="yt-heading">{t('videosPage.heading')}</h1>
      <div className="yt-layout">
        {/* Main player */}
        <div className="yt-player">
          {active && (
            <div className="yt-embed-wrap">
              <iframe
                key={active}
                src={`https://www.youtube.com/embed/${active}?autoplay=1&rel=0`}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </div>

        {/* Sidebar list */}
        <ul className="yt-list">
          {videos.map(v => (
            <li
              key={v.id}
              className={`yt-item${v.id === active ? ' yt-item--active' : ''}`}
              onClick={() => setActive(v.id)}
            >
              <div className="yt-thumb-wrap">
                <img
                  src={v.thumb}
                  alt={v.title}
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = thumbFor(v.id); }}
                />
                <span className="yt-play-icon">▶</span>
              </div>
              <div className="yt-item-info">
                <p className="yt-item-title">{v.title}</p>
                <p className="yt-item-date">{v.date}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
